import {Component} from '@angular/core';
import {UserCollectionsDatasource} from './user-collections-datasource';
import {UserCollectionsService} from './user-collections.service';
import {AuthService} from '../../auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {AppConfigService} from '../../../config/app-config.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {UserCollectionDtoInterface} from './dto/user-collection-dto';
import {MessageService} from '../../../messages/message.service';
import {FormControl} from '@angular/forms';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import * as moment from 'moment';
import {TranslateService} from '../../../shared/translate.service';
import {HtmlUtilsService} from '../../../html-utils/html-utils.service';
import {BaseUserTableView} from '../base-user-table-view';
import {FilterUserCollectionDto} from './dto/filter-user-collection-dto';
import {UserCollectionComponent} from './user-collection/user-collection.component';
import {AppUtils} from '../../../shared/app-utils';

@Component({
  selector: 'app-user-collections',
  templateUrl: './user-collections.component.html',
  styleUrls: ['./user-collections.component.scss']
})
export class UserCollectionsComponent extends BaseUserTableView<UserCollectionDtoInterface, FilterUserCollectionDto> {

  displayedColumns = ['id', 'name', 'description', 'creationTime', 'itemsCount', 'options'];

  nameField = new FormControl();
  descField = new FormControl();
  itemsCountFromField = new FormControl();
  itemsCountToField = new FormControl();
  creationFromField = new FormControl();
  creationToField = new FormControl();

  nameFieldValue = '';
  descFieldValue = '';
  itemsCountFromFieldValue = null;
  itemsCountToFieldValue = null;
  creationFromFieldValue = null;
  creationToFieldValue = null;


  constructor(protected userService: UserCollectionsService, public translator: TranslateService,
              protected authService: AuthService, protected route: ActivatedRoute, public htmlUtils: HtmlUtilsService,
              protected router: Router, protected configService: AppConfigService,
              protected modalService: NgbModal, protected messageService: MessageService
  ) {
    super(translator, configService, router, route, modalService, messageService, authService, userService);
    this.dataSource = new UserCollectionsDatasource(this.userService, this.authService);
  }

  getPrefix(): string {
    return 'item';
  }

  loadMore() {
    this.dataSource.loadCollections(this.page, this.pageSize, this.sort, this.direction,
      {
        name: this.nameFieldValue, desc: this.descFieldValue, itemsCountFrom: this.itemsCountFromFieldValue,
        itemsCountTo: this.itemsCountToFieldValue, creationFrom: '',
        creationTo: ''
      });
  }

  openCreateModal(){
    this.modalService.open(UserCollectionComponent).result.then(value => {
      if (value){
        this.authService.getToken().then(token => {
          this.userService.update(value, token).subscribe(result => {
            this.messageService.successMessage('SubmitChanges.title','SubmitChanges.desc');
            this.router.navigate(['/collections'], {queryParams: {f: AppUtils.generateRandom()}});
          }, error1 => {
            console.log(error1);
            this.messageService.errorMessage('UserCollection.permissionError.title','UserCollection.permissionError.desc');
          });
        });
      }
    });
  }

  openEditModal(element: UserCollectionDtoInterface){
    const modal = this.modalService.open(UserCollectionComponent);
    modal.componentInstance.userCollectionDto = element;
    modal.result.then(value => {
      if (value) {
        this.authService.getToken().then(token => {
          this.userService.handleSaveUserCollection(value, token, this.messageService).then(userCollectionDto => {
            if (userCollectionDto) {
              element.name = userCollectionDto.name;
              element.description = userCollectionDto.description;
              element.itemsCount = userCollectionDto.itemsCount;
            }
          });
        });
      }
    });
  }

  protected getDeletePrefix(): string {
    return 'UserCollection.delete';
  }

  protected initFilters() {
    this.nameField.setValue(this.nameFieldValue);
    this.descField.setValue(this.descFieldValue);
    this.itemsCountFromField.setValue(this.itemsCountFromFieldValue);
    this.itemsCountToField.setValue(this.itemsCountToFieldValue);
    this.creationFromField.setValue(this.creationFromFieldValue);
    this.creationToField.setValue(this.creationToFieldValue);

    this.nameField.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()).subscribe(value => {
      this.nameFieldValue = value;
      this.navigateByParams();
    });
    this.descField.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()).subscribe(value => {
      this.descFieldValue = value;
      this.navigateByParams();
    });
    this.itemsCountFromField.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()).subscribe(value => {
      this.itemsCountFromFieldValue = value;
      this.navigateByParams();
    });
    this.itemsCountToField.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()).subscribe(value => {
      this.itemsCountToFieldValue = value;
      this.navigateByParams();
    });
    this.creationFromField.valueChanges.pipe(
      distinctUntilChanged()
    ).subscribe(value => {
      this.creationFromFieldValue = value;
      this.navigateByParams();
    });
    this.creationToField.valueChanges.pipe(
      distinctUntilChanged()
    ).subscribe(value => {
      this.creationToFieldValue = value;
      this.navigateByParams();
    });
  }

  protected populateQueryParams(params) {
    const queryPageSize = params.get('pageSize');
    const querySort = params.get('sort');
    const queryDirection = params.get('direction');
    const page = params.get('page');
    this.nameFieldValue = params.get('nameFieldValue');
    this.descFieldValue = params.get('descFieldValue');
    this.itemsCountFromFieldValue = params.get('itemsCountFromFieldValue');
    this.itemsCountToFieldValue = params.get('itemsCountToFieldValue');
    if (params.get('creationFromFieldValue')) {
      this.creationFromFieldValue = moment(params.get('creationFromFieldValue'), this.dateFormat);
    }
    if (params.get('creationToFieldValue')) {
      this.creationToFieldValue = moment(params.get('creationToFieldValue'), this.dateFormat);
    }

    if (queryPageSize && this.pageSizeOptions.indexOf(+queryPageSize)) {
      this.pageSize = +queryPageSize;
    }
    if (querySort && this.displayedColumns.indexOf(querySort)) {
      this.sort = querySort;
    }
    if (page && +page > 0) {
      this.page = +page;
    }
    if (queryDirection && (queryDirection == 'asc' || queryDirection == 'desc')) {
      this.direction = queryDirection;
    }

    this.dataSource.loadCollections(this.page, this.pageSize, this.sort, this.direction,
      {
        name: this.nameFieldValue, desc: this.descFieldValue, itemsCountFrom: this.itemsCountFromFieldValue,
        itemsCountTo: this.itemsCountToFieldValue, creationFrom: params.get('creationFromFieldValue'),
        creationTo: params.get('creationToFieldValue')
      });
  }

  protected navigateByParams() {
    if (this.page>this.totalPages){
      this.page = this.totalPages;
    }else if (this.page<1){
      this.page = 1;
    }

    const queryParams = {
      page: this.page, pageSize: this.pageSize, sort: this.sort, direction: this.direction,
      nameFieldValue: this.nameFieldValue, descFieldValue: this.descFieldValue, f:AppUtils.generateRandom()
    };
    if (this.itemsCountFromFieldValue) {
      queryParams['itemsCountFromFieldValue'] = this.itemsCountFromFieldValue + '';
    }
    if (this.itemsCountToFieldValue) {
      queryParams['itemsCountToFieldValue'] = this.itemsCountToFieldValue + '';
    }
    if (this.creationFromFieldValue) {
      queryParams['creationFromFieldValue'] = this.creationFromFieldValue.format(this.dateFormat);
    }
    if (this.creationToFieldValue) {
      queryParams['creationToFieldValue'] = this.creationToFieldValue.format(this.dateFormat);
    }
    this.router.navigate(['/collections'], {
      queryParams: queryParams
    });
  }
}
